import React, { createContext, ReactNode, useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { IModulo, IProduct, ISkill } from '../interfaces/ISkills'
import { SkillsService } from '../services/methods/SkillsService'
import { generateColor } from '../utils/generateColor'

interface IOptions {
  codigo: string;
  tipo: "1" | "2" | "3";
  nome: string;
  codpai: string;
}

interface SkillsProviderProps {
  children: ReactNode
}

interface ISkillsContextData {
  products: IProduct[];
  modules: IModulo[];
  skills: ISkill[];
  moduloColor: string;
  isLoading: boolean;
  isLoadingModules: boolean;
  isLoadingSkills: boolean;
  fetchProducts: () => Promise<void>;
  fetchModules: (code: string) => Promise<void>;
  disFetchModules: (code: string) => Promise<void>;
  handleAddNewSkill: (itemSelected: IOptions, type: 'skill' | 'product' | 'module') => Promise<void>;
  handleDeleteSkill: (code: string) => void;
  handleDeleteModulo: (code: string) => void;
  handleCleanState: (state: 'skill' | 'product' | 'module') => Promise<void>;
  updateSkillLevel(level: number, code: string): void;
  codPai: string;
}

const SkillsContext = createContext({} as ISkillsContextData)

function SkillsProvider({ children }: SkillsProviderProps) {
  const skillsService: SkillsService = new SkillsService();

  const [products, setProducts] = useState<IProduct[]>([])
  const [modules, setModules] = useState<IModulo[]>([])
  const [skills, setSkills] = useState<ISkill[]>([])
  const [moduloColor, setModuloColor] = useState('')
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingModules, setIsLoadingModules] = useState(true)
  const [isLoadingSkills, setIsLoadingSkills] = useState(true)
  const [codPai, setCodPai] = useState('')

  async function fetchProducts() {
    const token = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
    try {
      setIsLoading(true);
      const { data } = await skillsService.getProduct(token!)
      setModuloColor(generateColor())
      if (data.status === 'ok') {
        setProducts(data.dados)
      } else {
        toast.error(data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }

      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchSkills(code: string, color: string) {
    const token = await sessionStorage.getItem('token')
    try {
      const { data } = await skillsService.getSkills(token || '', code)

      if (data.status === 'ok') {
        const skillsResponse = data.dados.map((skill: ISkill) => {
          skill.name = skill.name.toLowerCase()
          skill.moduloCode = code;
          skill.color = color
          return skill
        })

        setSkills(prevSkills => ([...prevSkills, ...skillsResponse]))

      } else {
        toast.error(data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      console.log(error)
    } finally {
      setIsLoadingSkills(false)
    }
  }

  async function fetchModules(code: string) {
    const token = await sessionStorage.getItem('token')
    try {
      const { data } = await skillsService.getGroups(token || '', code)

      if (data.status === 'ok') {
        const modulesResponse: IModulo[] = data.dados.map((modulo: IModulo) => {
          modulo.productCode = code;
          modulo.color = generateColor()
          return modulo
        })

        for (const modulo of modulesResponse) {
          await fetchSkills(modulo.code, modulo.color!)
        }

        const newModules = [
          ...modules,
          ...modulesResponse
        ]

        setModules(newModules)
      } else {
        toast.error(data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      console.log(error)
    } finally {
      setIsLoadingModules(false)
    }
  }

  async function disFetchSkill(modulos: IModulo[]) {
    const skillArrAtt: ISkill[] = []
    modulos.map(modulo => {
      skills.filter(skill => {
        if (skill.moduloCode === modulo.code) {
          skillArrAtt.push(skill)
        }
        return skill
      })
      return modulo
    })
    setSkills(skillArrAtt)
  }

  async function disFetchSkillsModule(code: string) {
    const skillArrAtt: ISkill[] = []
    skills.filter(skill => {
      if (skill.moduloCode !== code) {
        skillArrAtt.push(skill)
      }
      return skill
    })
    setSkills(skillArrAtt)
  }

  async function disFetchModules(code: string) {
    const modulesArrAtt: IModulo[] = []

    modules.map(modulo => {
      if (modulo.productCode !== code) {
        modulesArrAtt.push(modulo)
      }
      return modulo
    })
    setModules(modulesArrAtt)
    await disFetchSkill(modulesArrAtt)
  }

  async function reloadModules(code: string) {
    const modulesArrAtt: IModulo[] = []

    modules.map(modulo => {
      if (modulo.productCode !== code) {
        modulesArrAtt.push(modulo)
      }
      return modulo
    })

    await disFetchSkill(modulesArrAtt)

    const token = await sessionStorage.getItem('token')
    setIsLoadingModules(true)
    try {
      const { data } = await skillsService.getGroups(token || '', code)

      if (data.status === 'ok') {
        const modulesResponse: IModulo[] = data.group.map((modulo: IModulo) => {
          modulo.productCode = code;
          modulo.color = generateColor()
          return modulo
        })

        for (const modulo of modulesResponse) {
          await fetchSkills(modulo.code, modulo.color!)
        }

        const newModules = [
          ...modulesArrAtt,
          ...modulesResponse
        ]

        setModules(newModules)
      } else {
        toast.error(data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      console.log(error)
    } finally {
      setIsLoadingModules(false)
    }
  }

  async function handleDeleteSkill(code: string) {
    const token = await sessionStorage.getItem('token')
    const id = toast.loading('Por favor aguarde...')
    try {
      const skillArrAtt: ISkill[] = []

      skills.filter(skill => {
        if (skill.code !== code) {
          skillArrAtt.push(skill)
        }
        return skill
      })

      setSkills(skillArrAtt)

      const objectParams = {
        data: {
          token,
          data: {
            code
          }
        }
      }

      const { data } = await skillsService.deleteSkill(objectParams)

      if (data.status === 'ok') {
        toast.update(id, {render: data.msg, type: "success", isLoading: false, autoClose: 5000, closeOnClick: true });
      } else {
        toast.update(id, {render: data.errorMessage || data.msg, type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
      }
    } catch (error: any) {
      if (error.response) {
        toast.update(id, {render: error.response.data.errorMessage || 'Ops... algo deu errado!', type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
      }
      console.log(error)
    }

  }

  async function handleDeleteModulo(code: string) {
    const token = await sessionStorage.getItem('token')

    try {
      const modulesArrAtt: IModulo[] = []
      const skillArrAtt: ISkill[] = []

      modules.filter(modulo => {
        if (modulo.code !== code) {
          modulesArrAtt.push(modulo)
        }
        return modulo
      })

      skills.filter(skill => {
        if (skill.moduloCode !== code) {
          skillArrAtt.push(skill)
        }
        return skill
      })

      setModules(modulesArrAtt)
      setSkills(skillArrAtt)

      const objectParams = {
        data: {
          token,
          data: {
            code
          }
        }
      }

      const { data } = await skillsService.deleteSkill(objectParams)

      if (data.status === 'ok') {
        toast.success(data.msg)
      } else {
        toast.error(data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      console.log(error)
    }
  }

  async function handleAddNewSkill(itemSelected: IOptions, type: 'skill' | 'product' | 'module') {
    const token = await sessionStorage.getItem('token')

    const id = toast.loading('Por favor aguarde...');

    const objectParams = {
      token,
      data: {
        code: itemSelected.codigo,
        level: 1
      }
    }

    try {

      await setCodPai(itemSelected.codpai.trim())

      if (type === 'product') {
        const { data, status } = await skillsService.addProduct(objectParams)

        if (status === 200 || status === 201) {
          toast.update(id, {render: data.msg, type: "success", isLoading: false, autoClose: 5000, closeOnClick: true });
          fetchProducts()
        } else {
          toast.update(id, {render: data.errorMessage || data.msg, type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
        }
      } else if (type === 'module') {
        await setIsLoadingModules(true)
        const newModulo: IModulo = {
          code: itemSelected.codigo.trim(),
          color: moduloColor,
          coment: "",
          level: 1,
          productCode: itemSelected.codpai.trim(),
          name: itemSelected.nome.trim(),
          type: itemSelected.tipo,
        }

        const { data, status } = await skillsService.addGroup(objectParams)

        if (status === 200 || status === 201) {
          setModuloColor(generateColor())
          setModules(prevModules => ([...prevModules, newModulo]))
          toast.update(id, {render: data.msg, type: "success", isLoading: false, autoClose: 5000, closeOnClick: true });
        } else {
          toast.update(id, {render: data.errorMessage || data.msg, type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
        }
      } else if (type === 'skill') {
        // setIsLoadingSkills(true)
        let colorSkill = '';
        modules.map(modulo => {
          if (modulo.code.trim() === itemSelected.codpai.trim()) {
            colorSkill = modulo.color
          }
          return modulo
        })

        const newSkill: ISkill = {
          code: itemSelected.codigo.trim(),
          color: colorSkill,
          coment: "",
          level: 1,
          moduloCode: itemSelected.codpai.trim(),
          name: itemSelected.nome.toLowerCase().trim(),
          type: itemSelected.tipo,
        }

        const { data, status } = await skillsService.addSkill(objectParams)

        if (status === 200 || status === 201) {
          setSkills(prevSkills => ([...prevSkills, newSkill]))
          toast.update(id, {render: data.msg, type: "success", isLoading: false, autoClose: 5000, closeOnClick: true });
        } else {
          toast.update(id, {render: data.errorMessage || data.msg, type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
        }
      } else {
        toast.update(id, {render: 'Ops... algo deu errado!', type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });

        toast.error('Ops... algo deu errado!')
      }
    } catch (error: any) {
      if (error.response) {
        toast.update(id, {render: error.response.data.errorMessage || 'Ops... algo deu errado!', type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
      }
      console.log(error)
    } finally {
      setIsLoadingSkills(false)
      setIsLoadingModules(false)
    }
  }

  async function handleCleanState(stateSelected: 'skill' | 'product' | 'module') {
    switch (stateSelected) {
      case 'skill':
        setSkills([]);
        break;
      case 'module':
        setModules([]);
        break;
      case 'product':
        setProducts([]);
        break;
    }
  }

  async function updateSkillLevel(level: 1 | 2 | 3 | 4 | 5, code: string) {
    const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
    const id = toast.loading('Por favor aguarde...')
    try {
      const objectParams = {
        token: userToken,
        data: {
          code: code,
          level
        }
      }
      const oldSkills = skills
      const skillUpdated = skills.find(skill => skill.code === code)
      const arrSkills = skills.filter(skill => skill.code !== code)
      skillUpdated!.level = level

      setSkills([...arrSkills, skillUpdated!])


      const { data } = await skillsService.updateSkillLevel(objectParams)

      if (data.status === 'ok') {
        toast.update(id, {render: data.msg, type: "success", isLoading: false, autoClose: 5000, closeOnClick: true });
      } else {
        toast.update(id, {render: data.errorMessage || data.msg, type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
        setSkills(oldSkills)
      }


    } catch (error: any) {
      if (error.response) {
        toast.update(id, {render: error.response.data.errorMessage || 'Ops... algo deu errado!', type: "error", isLoading: false, autoClose: 5000, closeOnClick: true });
      }

      console.log(error)
    }
  }

  return (
    <SkillsContext.Provider value={{
      products,
      modules,
      skills,
      moduloColor,
      isLoading,
      isLoadingModules,
      isLoadingSkills,
      fetchProducts,
      fetchModules,
      disFetchModules,
      handleAddNewSkill,
      handleDeleteSkill,
      handleDeleteModulo,
      handleCleanState,
      updateSkillLevel,
      codPai
    }}>
      {children}
    </SkillsContext.Provider>
  )
}

function useSkills() {
  const context = useContext(SkillsContext)

  return context
}

export { SkillsProvider, useSkills }